import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  Typography,
} from '@material-ui/core';
import {
  AccountBalance,
  Business,
  Check,
  Close,
  Language,
  Mail,
  Payment,
  Person,
  Phone,
  Room,
  Star,
} from '@material-ui/icons';
import { FormattedDate } from '@superdispatch/dates';
import { formatPaymentMethod } from '@superdispatch/sdk';
import { Column, Columns, Stack } from '@superdispatch/ui';
import { Box, DescriptionItem, MultilineText } from '@superdispatch/ui-lab';
import { truncate } from 'es-toolkit/compat';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'shared/helpers/RoutingHelpers';
import { CalendarMonthIcon } from 'shared/icons';
import { HashtagIcon } from 'shared/icons/HashtagIcon';
import {
  formatInBusinessSince,
  formatShipperAddress,
  ShipperProfileDTO,
} from 'shared/modules/shipper/ShipperProfileDTO';
import {
  WebViewLink,
  WebViewLinkedText,
  WebViewPhoneLink,
} from 'shared/routing/WebViewLinks';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { joinStrings } from 'shared/utils/StringUtils';
import { trackLoadboardEvent } from '../LoadboardAnalytics';
import { useShipperProfileAnalytics } from './data/ShipperProfileAnalytics';
import { ShipperProfileCard } from './ShipperProfileCard';
import { ShipperProfileRequirements } from './ShipperProfileRequirements';
import { ShipperReportingBlock } from './ShipperReporting';

export function ShipperProfileOverview({
  data: shipperProfile,
}: {
  data: ShipperProfileDTO;
}) {
  const navigate = useNavigate();
  const [{ utm_medium }] = useQuery();
  const [isTermsDialogOpen, setIsTermsDialogOpen] = useState(false);
  const { data: carrierSettings } = useCarrierSettings();

  const trackShipperEvent = useShipperProfileAnalytics(shipperProfile);

  const isMobileView = utm_medium === 'webview' || utm_medium === 'mobile'; // Show header only for webview. Hide by default as user could come from mobile native.
  const termsTruncated = useMemo(
    () =>
      shipperProfile.terms_and_conditions &&
      truncate(shipperProfile.terms_and_conditions, {
        length: 175,
        omission: '…',
      }),
    [shipperProfile],
  );

  const shipperProfileAddress = useMemo(
    () => formatShipperAddress(shipperProfile),
    [shipperProfile],
  );

  const hasProfileAboutUsInfo = !!(
    shipperProfileAddress ||
    shipperProfile.about_us ||
    shipperProfile.in_business_since ||
    shipperProfile.usdot_number ||
    shipperProfile.personal_page_url
  );

  const hasBillingInfo = !!(
    shipperProfile.billing_contact_name ||
    shipperProfile.billing_email ||
    shipperProfile.billing_phone
  );

  const hasPaymentMethodsInfo = !!(
    shipperProfile.alternative_payment_methods?.length ||
    shipperProfile.preferred_payment_methods?.length
  );

  const hasPaymentInfo = hasBillingInfo || hasPaymentMethodsInfo;

  const hasBondInsuranceInfo = !!(
    shipperProfile.bond_company ||
    shipperProfile.bond_agent ||
    shipperProfile.agent_phone ||
    shipperProfile.bond_expiration_date
  );

  const companyType = shipperProfile.company_type;
  const shouldShowInsuranceInfo =
    companyType === 'BROKER' || companyType === 'DEALER';

  return (
    <>
      <Dialog
        fullWidth={true}
        open={isTermsDialogOpen}
        onClose={() => {
          setIsTermsDialogOpen(false);
        }}
      >
        <DialogTitle>
          <Columns align="center" space="small">
            <Column>
              <Typography variant="h3">Terms and Conditions</Typography>
            </Column>

            <Column width="content">
              <IconButton
                size="small"
                onClick={() => {
                  setIsTermsDialogOpen(false);
                }}
              >
                <Close />
              </IconButton>
            </Column>
          </Columns>
        </DialogTitle>

        <DialogContent>
          <DialogContentText variant="body2" color="textPrimary">
            <MultilineText>
              <WebViewLinkedText
                onClick={() => {
                  trackShipperEvent(
                    'SLB: Clicked Link in Terms and Conditions in Shipper Profile',
                  );
                }}
              >
                {shipperProfile.terms_and_conditions as string}
              </WebViewLinkedText>
            </MultilineText>
          </DialogContentText>
        </DialogContent>
      </Dialog>

      <Columns collapseBelow="desktop" space="small">
        <Column>
          <Stack space="small">
            <ShipperProfileCard title="About Us">
              {!hasProfileAboutUsInfo ? (
                <Typography color="textSecondary">
                  No information provided
                </Typography>
              ) : (
                <Stack space="small">
                  {shipperProfile.about_us && (
                    <Box
                      paddingLeft="small"
                      paddingRight="small"
                      paddingTop="xsmall"
                      paddingBottom="xsmall"
                      borderRadius="small"
                      backgroundColor="Blue50"
                      aria-label={`about ${shipperProfile.name}`}
                    >
                      <MultilineText>
                        <WebViewLinkedText
                          onClick={() => {
                            trackShipperEvent(
                              'SLB: Clicked Link in About-Us in Shipper Profile',
                            );
                          }}
                        >
                          {shipperProfile.about_us}
                        </WebViewLinkedText>
                      </MultilineText>
                    </Box>
                  )}

                  <Stack space={['xsmall', 'xxsmall']}>
                    {shipperProfile.in_business_since && (
                      <DescriptionItem
                        icon={<CalendarMonthIcon />}
                        label="In business since"
                      >
                        {formatInBusinessSince(
                          shipperProfile.in_business_since,
                        )}
                      </DescriptionItem>
                    )}
                    {shipperProfile.usdot_number && (
                      <DescriptionItem icon={<HashtagIcon />} label="USDOT">
                        {shipperProfile.usdot_number}
                      </DescriptionItem>
                    )}
                    {shipperProfile.personal_page_url && (
                      <DescriptionItem icon={<Language />}>
                        <WebViewLink
                          aria-label="personal page"
                          href={shipperProfile.personal_page_url}
                          target="_blank"
                          rel="noreferrer"
                          onClick={() => {
                            trackShipperEvent(
                              'SLB: Clicked Personal Page URL in Shipper Profile',
                            );
                          }}
                        >
                          {shipperProfile.personal_page_url}
                        </WebViewLink>
                      </DescriptionItem>
                    )}
                    {shipperProfileAddress && (
                      <DescriptionItem icon={<Room />} aria-label="address">
                        {shipperProfileAddress}
                      </DescriptionItem>
                    )}
                  </Stack>
                </Stack>
              )}
            </ShipperProfileCard>

            {carrierSettings?.carrier.verification_status === 'approved' && (
              <ShipperReportingBlock shipper={shipperProfile} />
            )}

            <ShipperProfileCard title="Payment Info">
              {!hasPaymentInfo ? (
                <Typography color="textSecondary">
                  No information provided
                </Typography>
              ) : (
                <Stack space="small">
                  {hasBillingInfo && (
                    <Stack>
                      <Typography variant="h6" color="textSecondary">
                        Billing
                      </Typography>

                      <Stack space={['xsmall', 'xxsmall']}>
                        {shipperProfile.billing_contact_name && (
                          <DescriptionItem
                            icon={<Person />}
                            aria-label="billing contact name"
                          >
                            {shipperProfile.billing_contact_name}
                          </DescriptionItem>
                        )}
                        {shipperProfile.billing_email && (
                          <DescriptionItem
                            icon={<Mail />}
                            aria-label="billing contact email"
                          >
                            <WebViewLink
                              href={`mailto:${shipperProfile.billing_email}`}
                            >
                              {shipperProfile.billing_email}
                            </WebViewLink>
                          </DescriptionItem>
                        )}
                        {shipperProfile.billing_phone && (
                          <DescriptionItem
                            icon={<Phone />}
                            aria-label="billing contact phone"
                          >
                            <WebViewPhoneLink
                              tel={shipperProfile.billing_phone}
                            />
                          </DescriptionItem>
                        )}
                      </Stack>
                    </Stack>
                  )}

                  {hasPaymentMethodsInfo && (
                    <Stack>
                      <Typography variant="h6" color="textSecondary">
                        Payment Methods
                      </Typography>

                      <Stack space={['xsmall', 'xxsmall']}>
                        {!!shipperProfile.preferred_payment_methods?.length && (
                          <DescriptionItem
                            icon={<Star />}
                            label="Preferred"
                            wrap={true}
                          >
                            {joinStrings(
                              ', ',
                              ...shipperProfile.preferred_payment_methods.map(
                                (method) => formatPaymentMethod(method),
                              ),
                            )}
                          </DescriptionItem>
                        )}

                        {!!shipperProfile.alternative_payment_methods
                          ?.length && (
                          <DescriptionItem
                            icon={<Payment />}
                            label="Alternatives"
                            wrap={true}
                          >
                            {joinStrings(
                              ', ',
                              ...shipperProfile.alternative_payment_methods.map(
                                (method) => formatPaymentMethod(method),
                              ),
                            )}
                          </DescriptionItem>
                        )}
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              )}
            </ShipperProfileCard>

            {shouldShowInsuranceInfo && (
              <ShipperProfileCard title="Bond">
                {!hasBondInsuranceInfo ? (
                  <Typography color="textSecondary">
                    Request this information from the shipper
                  </Typography>
                ) : (
                  <Stack space={['xsmall', 'xxsmall']}>
                    {shipperProfile.verification_status === 'APPROVED' &&
                      !!shipperProfile.usdot_number && (
                        <DescriptionItem
                          wrap={true}
                          icon={<Check />}
                          label="FMCSA Verified"
                        >
                          Insurance on File
                        </DescriptionItem>
                      )}
                    {shipperProfile.bond_expiration_date &&
                      shipperProfile.company_type === 'DEALER' && (
                        <DescriptionItem
                          icon={<CalendarMonthIcon />}
                          label="Expiration Date"
                        >
                          <FormattedDate
                            variant="Date"
                            format="DateISO"
                            date={shipperProfile.bond_expiration_date}
                          />
                        </DescriptionItem>
                      )}
                    {shipperProfile.bond_company && (
                      <DescriptionItem
                        wrap={true}
                        icon={<AccountBalance />}
                        label="Company"
                      >
                        {shipperProfile.bond_company}
                      </DescriptionItem>
                    )}
                    {shipperProfile.bond_agent && (
                      <DescriptionItem icon={<Person />} label="Agent">
                        {shipperProfile.bond_agent}
                      </DescriptionItem>
                    )}
                    {shipperProfile.agent_phone && (
                      <DescriptionItem icon={<Phone />}>
                        <WebViewPhoneLink
                          aria-label="bond agent phone"
                          tel={shipperProfile.agent_phone}
                        />
                      </DescriptionItem>
                    )}
                  </Stack>
                )}
              </ShipperProfileCard>
            )}
          </Stack>
        </Column>

        <Column>
          <Stack space="small">
            <ShipperProfileCard title="Contact Info">
              <Stack space={['xsmall', 'xxsmall']}>
                <DescriptionItem icon={<Person />} aria-label="contact name">
                  {shipperProfile.contact_name}
                </DescriptionItem>
                <DescriptionItem icon={<Mail />} aria-label="contact email">
                  <WebViewLink href={`mailto:${shipperProfile.contact_email}`}>
                    {shipperProfile.contact_email}
                  </WebViewLink>
                </DescriptionItem>
                <DescriptionItem icon={<Phone />} aria-label="contact phone">
                  <WebViewPhoneLink
                    tel={shipperProfile.contact_phone}
                    onClick={() => {
                      trackLoadboardEvent({
                        name: 'SLB: Clicked Shipper Phone In Shipper Profile',
                      });
                    }}
                  >
                    {shipperProfile.contact_phone}
                  </WebViewPhoneLink>
                </DescriptionItem>
              </Stack>
            </ShipperProfileCard>

            <ShipperProfileRequirements
              shipperProfile={shipperProfile}
              onRequirementLinkClick={() => {
                trackShipperEvent(
                  'SLB: Clicked Link in Additional Carrier Requirements in Shipper Profile',
                );
              }}
            />

            <ShipperProfileCard title="References">
              {!shipperProfile.referrals?.length ? (
                <Typography color="textSecondary">
                  No information provided
                </Typography>
              ) : (
                <Stack space="large">
                  {shipperProfile.referrals.map((referral, idx) => (
                    <Stack
                      key={idx}
                      space={['xsmall', 'xxsmall']}
                      aria-label={`reference-${idx + 1}`}
                    >
                      {referral.name && (
                        <DescriptionItem icon={<Business />} label="Company">
                          {referral.name}
                        </DescriptionItem>
                      )}

                      {referral.contact_name && (
                        <DescriptionItem icon={<Person />} label="Contact">
                          {referral.contact_name}
                        </DescriptionItem>
                      )}

                      {referral.phone && (
                        <DescriptionItem icon={<Phone />}>
                          <WebViewPhoneLink
                            aria-label="phone"
                            tel={referral.phone}
                          />
                        </DescriptionItem>
                      )}
                    </Stack>
                  ))}
                </Stack>
              )}
            </ShipperProfileCard>

            <ShipperProfileCard title="Terms and Conditions">
              {!termsTruncated ? (
                <Typography color="textSecondary">
                  No information provided
                </Typography>
              ) : (
                <Stack space="small">
                  <Typography>
                    <MultilineText>{termsTruncated}</MultilineText>
                  </Typography>

                  <Link
                    color="primary"
                    component="button"
                    onClick={() => {
                      if (isMobileView) {
                        navigate(
                          `/terms-and-conditions/${shipperProfile.guid}`,
                        );
                      } else {
                        setIsTermsDialogOpen(true);
                      }
                    }}
                  >
                    Read more
                  </Link>
                </Stack>
              )}
            </ShipperProfileCard>
          </Stack>
        </Column>
      </Columns>
    </>
  );
}
